export type Capability =
  | "image.text_to_image"
  | "image.image_to_image"
  | "image.remove_background"
  | "text.chat"
  | "text.summarize"
  | "text.translate"
  | "text.prompt_builder"
  | "document.analysis"
  | "audio.text_to_speech"
  | "audio.speech_to_speech"
  | "audio.transcription"
  | "video.text_to_video"
  | "video.image_to_video";

export type ModelItem = {
  model_id: string;
  provider: string;
  display_name: string;
  description?: string | null;
  input_modalities: string[];
  output_modalities: string[];
  supported_parameters: string[];
  supported_aspect_ratios: string[];
  estimated_cost: string;
  is_available: boolean;
  is_default: boolean;
  quality_score: number;
  speed_score: number;
  cost_score: number;
  canonical_slug?: string | null;
  context_length?: number | null;
  capabilities?: Record<string, any> | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_seen_at?: string | null;
  metadata?: Record<string, any> | null;
};

export function supportsCapability(model: ModelItem, capability: Capability): boolean {
  const input = new Set(model.input_modalities);
  const output = new Set(model.output_modalities);
  if (capability === "image.text_to_image") return input.has("text") && output.has("image");
  if (capability === "image.image_to_image") return input.has("image") && output.has("image");
  if (capability === "image.remove_background") return input.has("image") && output.has("image");
  if (capability.startsWith("text.")) return input.has("text") && output.has("text");
  if (capability === "document.analysis") return (input.has("file") || input.has("text")) && output.has("text");
  if (capability === "audio.text_to_speech") return input.has("text") && output.has("audio");
  if (capability === "audio.speech_to_speech") return input.has("audio") && output.has("audio");
  if (capability === "audio.transcription") return input.has("audio") && output.has("text");
  if (capability === "video.text_to_video") return input.has("text") && output.has("video");
  if (capability === "video.image_to_video") return input.has("image") && output.has("video");
  return false;
}

export function filterModels(
  models: ModelItem[],
  query: string,
  provider: string,
  capability: Capability
): ModelItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  return models.filter((model) => {
    const matchesQuery =
      !normalizedQuery ||
      model.model_id.toLowerCase().includes(normalizedQuery) ||
      model.display_name.toLowerCase().includes(normalizedQuery);
    const matchesProvider = provider === "all" || model.provider === provider;
    return model.is_available && matchesQuery && matchesProvider && supportsCapability(model, capability);
  });
}

export function sortModels(models: ModelItem[], mode: string): ModelItem[] {
  const copy = [...models];
  if (mode === "cheap") return copy.sort((a, b) => Number(a.estimated_cost) - Number(b.estimated_cost));
  if (mode === "fast") return copy.sort((a, b) => b.speed_score - a.speed_score);
  if (mode === "quality") return copy.sort((a, b) => b.quality_score - a.quality_score);
  if (mode === "provider") return copy.sort((a, b) => a.provider.localeCompare(b.provider));
  return copy.sort((a, b) => Number(b.is_default) - Number(a.is_default) || b.quality_score - a.quality_score);
}

export function filterCatalogModels(
  models: ModelItem[],
  query: string,
  provider: string,
  tab: string
): ModelItem[] {
  const normalizedQuery = query.trim().toLowerCase();
  return models.filter((model) => {
    const matchesQuery =
      !normalizedQuery ||
      model.model_id.toLowerCase().includes(normalizedQuery) ||
      model.display_name.toLowerCase().includes(normalizedQuery);
    const matchesProvider = provider === "all" || model.provider === provider;
    
    let matchesTab = true;
    if (tab === "text") matchesTab = model.output_modalities.includes("text");
    else if (tab === "image") matchesTab = model.output_modalities.includes("image");
    else if (tab === "video") matchesTab = model.output_modalities.includes("video");
    else if (tab === "audio") matchesTab = model.output_modalities.includes("audio");
    else if (tab === "file") matchesTab = model.input_modalities.includes("file");
    else if (tab === "json") {
      const params = model.supported_parameters || [];
      matchesTab = params.includes("structured_output") || params.includes("tools") || params.includes("response_format");
    }
    
    return model.is_available && matchesQuery && matchesProvider && matchesTab;
  });
}
